import type { FC } from 'react'
import { Box, Text } from '@chakra-ui/react'
import type { ChakraProps } from '@chakra-ui/react'
import { useTranslation, Trans } from 'react-i18next'
import { getEnhancedSERPOfficeType } from './enhancementutils'

const VALID_URLS = ['stockholm'] as const

type ValidAreaURL = typeof VALID_URLS[number]
type OfficeType = 'office' | 'serviced_office'

function getIsValidAreaUrl(url: string | undefined): url is ValidAreaURL {
	if (!url) {
		return false
	}
	return VALID_URLS.includes(url.toLocaleLowerCase() as ValidAreaURL)
}

const FAQ_DATA: {
	[key in ValidAreaURL]: {
		[key in OfficeType]: { question: string; answer: string }[]
	}
} = {
	stockholm: {
		office: [
			{
				question: 'Vad påverkar kontorshyran?',
				answer:
					'Det finns två primära drivare av hyran på kontor - läge och vad som ingår. När det kommer till läge är skillnaden mellan 300-500% på Stockholms billigaste och dyraste adresser. Därutöver är det en nästa lika stor påverkan på vad som ingår och nivån på det hela, till och med så att 2 kontor i samma stadsdel kan skilja sig så mycket som 100% i hyra om ena är premium och den andra budget. Några exempel på saker som driver upp hyran: hur mycket gemensamma ytor det finns att nyttja, reception, frukost, fin inredning.',
			},
			{
				question: 'Vad ingår i en kontorshyra?',
				answer:
					'Alla kontorshyror som visas på yta.se är alltid totalkostnader, så att ni inte behöver oroa er över tillkommande utgifter. Väljer ni kontorsrum eller en egen avdelning på ett kontorshotell ingår i regel möbler, städning, internet, kaffe och tillgång till gemensamma utrymmen så som mötesrum och lounge. Ibland lyxas det till med frukost, gym, gruppträning med mera. Hyr ni ett eget kontor så är valet oftast att antingen hyra det med endast kortare avtalstid, eller att direkt via fastighetsägaren få hjälp med möblering och kontorstjänster till ett fast pris.',
			},
			{
				question: 'Hur långa är avtalstiderna?',
				answer:
					'Avtalstiderna följer i regel storlek på kontoret men varierar mellan 1–24 månader. Hyr ni ett kontor för 1–5 personer kan ni räkna med 1–12 månaders avtalstid, med bättre pris desto längre ni binder er. För mellanstora lösningar 6–15 personer är det fortfarande möjligt med ned till 1 månads uppsägningstid men normen är att signera 1 år. Är ni fler än 15 är det alltsomoftast 12–24 månader som gäller då dessa kontor ofta skräddarsys utefter era behov.',
			},
			{
				question: 'Hur gör jag en så bra kontorsdeal som möjligt?',
				answer:
					'Stockholms kontorsmarknad är störst i Sverige och med det ganska mogen, men det finns några bra riktlinjer för att säkerställa en bra deal. Gör ett grundligt förarbete genom att förstå ert kontorsbehov, kravställ det mot marknaden, och gå sedan på så många visningar som möjligt. Då får ni en förståelse kring hur mycket yta ni behöver och vilka aktörer som passar er. Är något av era alternativ en nyöppning? Det kan ofta ge något bättre rabatter. Var noga i förhandlingen att få med kringkostnader såsom service eller medlemskap. Men ert allra bästa verktyg? Förhandla med hyresvärden om ett så långt avtal ni kan tänka er för lägsta möjliga pris.',
			},
			{
				question: 'Vad innebär flexibla kontor i Stockholm?',
				answer:
					'Flexibla egna kontor är egna kontorsytor med kortare och flexibla avtal och möjlighet att paketera tjänster och möbler i hyran. Ni får en professionell motpart som sköter driften medan ni fokuserar på verksamheten. Segmentet har vuxit i Stockholm drivet av behovet av anpassningsbarhet och lägre initiala åtaganden.',
			},
			{
				question:
					'Vad kostar det att hyra ett flexibelt eget kontor i Stockholm?',
				answer:
					'Jämför alltid total månadskostnad (hyra, drift och tjänster) snarare än bara kronor per kvadratmeter. Som tumregel ligger nivåerna ungefär så här: City/CBD omkring 7 500–12 500 kr per person och månad, inom tull utanför City omkring 4 500–8 000 kr per person och månad, och utanför tull omkring 2 500–5 500 kr per person och månad. Flexupplägg innebär ofta lägre deposition och eliminerar stora initiala investeringar, till exempel möbler, IT och bemanning. Se objektsidorna för aktuella och verifierade priser.<br/><br/>Källa: Primärdata, riktiga priser direkt från operatörer och fastighetsägare som uppdaterar direkt på Yta.se via Yta.se IMS. Intervallen bygger på Yta.se:s realtidsdatabas och IMS med aggregering av listade objekt och inrapporterade affärer.',
			},
			{
				question: 'Prisvärda egna kontor i Stockholm',
				answer:
					'Vill ni prioritera pris, fokusera på lägen utanför tull och enklare servicenivå. I närförorter som Kista, Sollentuna, Hammarby Sjöstad och Bromma/Sundbyberg finns ett brett utbud av prisdrivna flexupplägg och traditionella kontor med konkurrenskraftiga villkor. Inom tull pressas kostnaden bäst genom yteffektiv planlösning och enklare service snarare än premiumadress.',
			},
			{
				question: 'Stockholms dyraste kontor',
				answer:
					'För premiumlösningar med representativ adress och hög standard finns många alternativ i Östermalm, Norrmalm och City. Här avgör läge, byggnad och servicenivå ofta priset. Säkerställ att kostnaden motsvarar affärsnyttan, till exempel kundflöde, säkerhets- eller teknikkrav och representativitet.',
			},
		],
		serviced_office: [
			{
				question:
					'Hur brukar avtalsvillkoren se ut på kontorshotell i Stockholm?',
				answer:
					'De flesta hyresavtalen på kontorshotell i Stockholm är lika då marknaden är så mogen. Generellt så inkluderar de en bindningstid på 0–12 månader, en uppsägningstid på 1–6 månader, en deposition på 1–2 månadshyror, vilka tjänster kontorshotellet inkluderar, samt en fast indexering av hyreskostnaden per år. Det allra vanligaste är att teckna ett 12 månaders-avtal åt gången och sedan förhandla med sin hyresvärd efter ett år om en ny förlängning om man trivs.',
			},
			{
				question: 'Hur ser processen ut att hyra kontor på kontorshotell?',
				answer:
					'Med tanke på att marknaden är så stor i Stockholm liknar sig processen att köpa boende, allt handlar om att komma ut på många visningar för att bilda sig en uppfattning om marknaden. Vi brukar rekommendera våra kunder att gå på minst 3-5 visningar, något som enkelt ordnas genom att boka direkt på yta.se eller med hjälp av en av våra kontorsexperter. Räkna med att det endast tar någon vecka från start till slut och att ni kan flytta in kort därpå.',
			},
			{
				question: 'Hur går det till att hyra kontorshotell i Stockholm?',
				answer:
					'Processen är enkel och snabb: börja med en behovsanalys kring läge, budget, arbetsplatser och servicenivå. På Yta.se kan ni söka själva i det samlade utbudet eller få kostnadsfri vägledning av en kontorsexpert. Vi tar fram en shortlist med priser och bokar 3-5 visningar så att ni kan jämföra planlösning, standard och lägesnytta sida vid sida. När ni väljer anläggning kan avtal ofta signeras snabbt och inflytt sker kort därefter.',
			},
			{
				question: 'Prisvärda kontorshotell i Stockholm',
				answer:
					'Stockholm rymmer många kostnadseffektiva kontorshotell. För privata rum ligger typiska nivåer i City omkring 6 000-12 000 kr per person och månad, inom tull utanför City omkring 3 500-8 000 kr per person och månad, och utanför tull omkring 2 000-6 000 kr per person och månad. Medlemskap i öppna ytor ligger ofta omkring 500-1 500 kr per person och månad beroende på läge och åtkomst. På Yta.se ser du alltid uppdaterade objekt med faktiska priser.',
			},
			{
				question: 'Exklusiva kontorshotell i Stockholm',
				answer:
					'För bolag som prioriterar adress, design och service erbjuder Stockholm premiumanläggningar en nivå som kan liknas vid en femstjärnig upplevelse: conciergetjänster, baristaservice och toppklassiga mötesmiljöer i City, Norrmalm och Östermalm och andra attraktiva lägen. Typiska nivåer i premiumsegmentet ligger omkring 10 000–20 000 kr per person och månad, med variationer mellan aktörer beroende på tjänsteinnehåll och läge. Några exempel på exklusiva kontorshotell i Stockholm, utan att vara komplett, är: Cecil, Convendum, No18 och A-House.',
			},
		],
	},
}

const OFFICE_TYPE_DATA: {
	[key in ValidAreaURL]: {
		[key in OfficeType]: {
			title: { heading: string; subHeading: string }
			types: { type: string; description: string }[]
		}
	}
} = {
	stockholm: {
		office: {
			title: {
				heading: 'Olika typer av kontor i Stockholm - vad finns att välja på?',
				subHeading:
					'I grund och botten finns det 3 olika typer av kontor att välja på; kontorsrum och egna avdelningar på kontorshotell och coworking, samt egna kontor som ni hyr direkt från fastighetsägare. Alla kontor på yta.se kommer alltid med flexibla avtalslängder och möjlighet till full service, så att ni slipper binda upp er på långa avtal.',
			},
			types: [
				{
					type: 'Kontorsrum',
					description:
						'Kontorsrum är den enklaste produkten och möjliggör att du snabbt kan flytta in och få full service, men har ändå ett eget låsbart rum som blir ert egna. Ni hyr ett eller flera rum i kombination och får utöver det tillgång till gemensamma ytor så som mötes- och konferensrum, loungeytor, kök och toaletter. Vanligtvis räknar man 3-5kvm per person i egna kontorsrum. Kontorsrum är bäst lämpad som lösning för företag som är söker upp till 15st arbetsplatser.',
				},
				{
					type: 'Egen avdelning',
					description:
						'En egen avdelning är ett kontor anpassat för större team men som fortfarande är på ett kontorshotell eller coworking space. Det är en hybrid mellan ett eget kontor och privata kontorsrum, där det ofta ingår egna mötesrum, egen loungeyta, egen entré, helt enkelt mer kontorsyta för att få det att kännas som ert. Ofta kan ni vara med och anpassa utformningen utefter era behov, vilket dock kan kräva att ni binder er 1-2 år. Rekommenderad kontorsyta per person är ca 5-7kvm, och vi rekommenderar denna lösning för team mellan 10-100 anställda.',
				},
				{
					type: 'Egna kontor',
					description:
						'Egna kontor är den största delen av kontorsmarknaden, på yta.se är vi experter på och presenterar endast egna kontor där det finns möjlighet att hyra med kortare avtalstider och lägga till service efter önskemål. Till skillnad från ett traditionellt kontor som kräver ett hyresavtal på 3-5 år, investeringar i möbler och infrastruktur, kontinuerlig drift och underhåll av kontoret, så sköts allt detta istället av er hyresvärd. Det innebär att ni kan mot en liten premie på hyran fokusera helt på er core business och se till att ni behåller er flexibilitet i att flytta till större eller mindre kontor när ert behov förändras.<br/><br/>Då egna kontor inte ger er access till gemensamma ytor behöver ni ha allt inom eran yta, vilket innebär att man får räkna med ca 10kvm/person. Vi rekommenderar team från 5 anställda och uppåt att titta på egna kontor, men i de mindre storlekarna kan det kräva att man har lite budget att arbeta med.',
				},
			],
		},
		serviced_office: {
			title: {
				heading: 'Vilka lösningar finns på kontorshotell i Stockholm?',
				subHeading:
					'I grund och botten finns det 3 olika lösningar att välja på inom kontorshotell och coworking; öppna arbetsplatser, kontorsrum eller egna avdelningar. Alla kontor på yta.se kommer alltid med flexibla avtalslängder och möjlighet till full service, så att ni slipper binda upp er på långa avtal.',
			},
			types: [
				{
					type: 'Öppna arbetsplatser',
					description:
						'Öppna arbetsplatser, eller medlemskap som det ibland kallas, innebär att ni får tillgång till kontorshotellet men inte något eget kontorsrum. Det finns många varianter på öppna arbetsplatser, allt ifrån att kunna nyttja loungeytor till fasta skrivbordsplatser i dedikerade kontorsrum som delas med andra medlemmar. Medlemskap är bäst lämpat för mindre team på 1-5 personer som värdesätter community och gemenskap.',
				},
				{
					type: 'Kontorsrum',
					description:
						'Kontorsrum är den vanligaste typen av lösning på kontorshotell, och innebär att ni hyr ett eller flera egna kontorsrum.  Utöver er privata kontorsyta får ni tillgång till gemensamma ytor så som mötes- och konferensrum, loungeytor, kök och toaletter. Vanligtvis räknar man 3-5kvm per person i egna kontorsrum. Kontorsrum är bäst lämpad som lösning för företag som är söker upp till 15st arbetsplatser.',
				},
				{
					type: 'Egen avdelning',
					description:
						'En egen avdelning är ett kontor anpassat för större team men som fortfarande är på ett kontorshotell eller coworking space. Det är en hybrid mellan ett eget kontor och privata kontorsrum, där det ofta ingår egna mötesrum, egen loungeyta, egen entré, helt enkelt mer space för att få det att kännas som ert space. Ofta kan ni vara med och anpassa utformningen utefter era behov, vilket dock kan kräva att ni binder er 1-2 år. Rekommenderad kontorsyta per person är ca 5-7kvm, och vi rekommenderar denna lösning för team mellan 10-100 anställda.',
				},
			],
		},
	},
}

const SERVICED_OFFICE_COMPARISON_DATA: {
	[key in ValidAreaURL]: {
		[key in OfficeType]: {
			title: { heading: string; subHeading: string }
			texts: { title: string; content: string }[]
		}
	}
} = {
	stockholm: {
		serviced_office: {
			title: {
				heading: 'Kontorshotell och coworking jämfört med traditionella kontor',
				subHeading:
					'Kontorshotell och coworking har vuxit snabbt de senaste åren i Stockholm, men är fortfarande ett relativt nytt fenomen för många. Idag är det den vanligaste lösningen för små och medelstora företag, nedan redogör vi för vad de kunder vi på yta.se hjälper brukar tänka kring för- och nackdelar med kontorshotell vs traditionellt kontor.',
			},
			texts: [
				{
					title: 'Fördelar med kontorshotell och coworking',
					content:
						'Den främsta anledningen till att välja kontorshotell och coworking är kortare hyresavtal samt att få alla kontorstjänster inkluderade. Ett traditionellt kontor kräver att hyresgästen både binder upp sig på ett 3-5 årigt avtal, men också att lösa allt som heter möblering, städ, internet, kaffe och andra penga- och tidskrävande tjänster. Utöver det är det vanligt med 6 månaders deposition på egna kontor, och ofta kring 1-3 månader på kontorshotell. Totalt sett blir det mycket kapital som behöver investeras i ett eget kontor.<br/><br/>På kontorshotell börjar hyresavtalen från 3 månader och ni får en enklare vardag då er hyresvärd löser allt det praktiska kring kontoret. Många hyresgäster uppskattar även möjligheten att använda kontorshotell som ett gummiband - både att skala upp och ned kontorsyta över tid men också att kunna hyra en mindre yta än totala arbetsstyrkan och använda sig av gemensamma ytor de dagar samtliga kollegor är samlade.',
				},
				{
					title: 'Nackdelar med kontorshotell jämfört med eget kontor',
					content:
						'Utav de företag vi hjälpt som flyttat vidare från kontorshotell till egna kontor brukar motivationen ligga i att bygga sitt eget varumärke och få mer yta för det egna teamet. Är ni 50 anställda eller fler börjar även utbudet av egna avdelningar att bli mindre, samtidigt som det finns ett stort utbud av egna kontor. Vill ni bygga egen kultur och få en större yta men inta tappa de flexibla avtalsvillkoren alternativt fortsatt få hjälp med service? Filtrera ovan på vår kategori för egna kontor där vi listar fullt servade egna kontor.',
				},
			],
		},
		office: {
			title: {
				heading: '',
				subHeading: '',
			},
			texts: [],
		},
	},
}

export const OfficeTypes: FC<
	{ pageType: string | undefined; areaUrl: string | undefined } & ChakraProps
> = ({ pageType = '', areaUrl, ...rest }) => {
	const { t } = useTranslation()
	const officeType = getEnhancedSERPOfficeType(pageType, t)
	if (
		!areaUrl ||
		!getIsValidAreaUrl(areaUrl) ||
		!officeType ||
		officeType === 'coworking_space'
	) {
		return null
	}
	const title = OFFICE_TYPE_DATA[areaUrl][officeType].title
	const types = OFFICE_TYPE_DATA[areaUrl][officeType].types
	return (
		<Box {...rest}>
			<Text fontSize="xl" fontWeight="bold" mb={4}>
				{t(title.heading)}
			</Text>
			<Text mb={4} color="grey.500">
				<Trans i18nKey={title.subHeading} />
			</Text>
			{types.map((officeType) => (
				<>
					<Text fontWeight="semibold" color="grey.500" mb={4}>
						{t(officeType.type)}
					</Text>
					<Text mb={4} color="grey.500">
						<Trans i18nKey={officeType.description} />
					</Text>
				</>
			))}
		</Box>
	)
}

export const CoworkingTradComparison: FC<
	{ pageType: string | undefined; areaUrl: string | undefined } & ChakraProps
> = ({ pageType = '', areaUrl, ...rest }) => {
	const { t } = useTranslation()
	const officeType = getEnhancedSERPOfficeType(pageType, t)
	if (
		!areaUrl ||
		!getIsValidAreaUrl(areaUrl) ||
		!officeType ||
		officeType !== 'serviced_office'
	) {
		return null
	}
	const title = SERVICED_OFFICE_COMPARISON_DATA[areaUrl][officeType].title
	const types = SERVICED_OFFICE_COMPARISON_DATA[areaUrl][officeType].texts
	return (
		<Box {...rest}>
			<Text fontSize="xl" fontWeight="bold" mb={4}>
				{t(title.heading)}
			</Text>
			<Text mb={4} color="grey.500">
				<Trans i18nKey={title.subHeading} />
			</Text>
			{types.map((officeType) => (
				<>
					<Text fontWeight="semibold" color="grey.500" mb={4}>
						{t(officeType.title)}
					</Text>
					<Text mb={4} color="grey.500">
						<Trans i18nKey={officeType.content} />
					</Text>
				</>
			))}
		</Box>
	)
}

export const FAQs: FC<
	{ pageType: string | undefined; areaUrl: string | undefined } & ChakraProps
> = ({ pageType = '', areaUrl, ...rest }) => {
	const { t } = useTranslation()
	const officeType = getEnhancedSERPOfficeType(pageType, t)
	if (
		!areaUrl ||
		!getIsValidAreaUrl(areaUrl) ||
		!officeType ||
		officeType === 'coworking_space'
	) {
		return null
	}
	return (
		<Box {...rest}>
			<OfficeTypes pageType={pageType} areaUrl={areaUrl} mb={8} />
			<CoworkingTradComparison pageType={pageType} areaUrl={areaUrl} mb={8} />
			{FAQ_DATA[areaUrl][officeType].map((faq) => (
				<>
					<Text fontSize="xl" fontWeight="bold" mb={4}>
						{t(faq.question)}
					</Text>
					<Text mb={4} color="grey.500">
						<Trans i18nKey={faq.answer} />
					</Text>
				</>
			))}
			<Text fontSize="xl" fontWeight="bold" mb={4}>
				{t('Datakällor och metod')}
			</Text>
			<Text mb={4} color="grey.500">
				<Trans
					i18nKey={
						'Livepriser publiceras och uppdateras dagligen av kontorshotell, operatörer och fastighetsägare direkt på Yta.se. Våra intervall och sammanställningar bygger på Yta.se:s realtidsuppdaterade databas och Inventory Management System (IMS), kompletterat med inrapporterade affärer och faktiska offerter. Vi visar typiska spann per område och produkt; nivåer kan variera mellan objekt och avtal.'
					}
				/>
			</Text>
			<Text mb={4} color="grey.500">
				{t(
					'Data: uppdateras dagligen • Texten senast faktagranskad: November 2025'
				)}
			</Text>
		</Box>
	)
}
